import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import './MisPedidosPage.css';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.origin.includes('localhost') ? 'http://localhost:8080/api' : `${window.location.origin}/api`);

const MisPedidosPage = () => {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const user = authService.getUser();
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/pedidos' } } });
      return;
    }

    const fetchPedidos = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_URL}/pedidos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error('Error al cargar pedidos');
        const data = await resp.json();
        setPedidos(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching pedidos:', err);
        setError('No se pudieron cargar tus pedidos.');
      } finally {
        setLoading(false);
      }
    };

    fetchPedidos();
  }, [navigate]);

  const getEstadoBadge = (estado) => {
    const config = {
      PENDIENTE: { label: 'Pendiente', icon: '⏳', cls: 'badge-pendiente' },
      PAGADO: { label: 'Pagado', icon: '✅', cls: 'badge-pagado' },
      ENVIADO: { label: 'Enviado', icon: '🚚', cls: 'badge-enviado' },
      ENTREGADO: { label: 'Entregado', icon: '📦', cls: 'badge-entregado' },
      CANCELADO: { label: 'Cancelado', icon: '❌', cls: 'badge-cancelado' },
    };
    const c = config[estado] || { label: estado, icon: '❓', cls: 'badge-pendiente' };
    return <span className={`pedido-badge ${c.cls}`}>{c.icon} {c.label}</span>;
  };

  const getEstadoStep = (estado) => {
    if (estado === 'CANCELADO') return -1;
    const steps = ['PENDIENTE', 'PAGADO', 'ENVIADO', 'ENTREGADO'];
    return steps.indexOf(estado);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency', currency: 'COP',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="pedidos-page">
        <div className="pedidos-container">
          <div className="pedidos-loading">
            <div className="pedidos-spinner"></div>
            <p>Cargando tus pedidos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pedidos-page">
      <div className="pedidos-container">
        <div className="pedidos-header">
          <h1>Mis Pedidos</h1>
          <p className="pedidos-subtitle">Historial y seguimiento de tus compras</p>
        </div>

        {error && (
          <div className="pedidos-error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Reintentar</button>
          </div>
        )}

        {pedidos.length === 0 && !error ? (
          <div className="pedidos-empty">
            <div className="empty-icon">🛍️</div>
            <h3>No tienes pedidos aún</h3>
            <p>Cuando realices una compra, tus pedidos aparecerán aquí.</p>
            <button className="btn-explorar" onClick={() => navigate('/productos')}>
              Explorar Productos
            </button>
          </div>
        ) : (
          <div className="pedidos-list">
            {pedidos.map((pedido) => {
              const isExpanded = expandedId === pedido.id;
              const step = getEstadoStep(pedido.estado);

              return (
                <div key={pedido.id} className={`pedido-card ${isExpanded ? 'expanded' : ''}`}>
                  {/* Header del pedido */}
                  <div className="pedido-card-header" onClick={() => setExpandedId(isExpanded ? null : pedido.id)}>
                    <div className="pedido-info-row">
                      <div className="pedido-id">
                        <span className="pedido-label">Pedido</span>
                        <span className="pedido-number">#{pedido.id}</span>
                      </div>
                      <div className="pedido-fecha">{formatDate(pedido.creado_en)}</div>
                    </div>
                    <div className="pedido-info-row">
                      <div className="pedido-total">{formatCurrency(pedido.total)}</div>
                      <div className="pedido-estado-badge">{getEstadoBadge(pedido.estado)}</div>
                    </div>
                    <div className="pedido-items-count">
                      {pedido.items?.length || 0} producto{(pedido.items?.length || 0) !== 1 ? 's' : ''}
                      <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Detalle expandido */}
                  {isExpanded && (
                    <div className="pedido-detail">
                      {/* Progress tracker or cancelled notice */}
                      {pedido.estado === 'CANCELADO' ? (
                        <div className="pedido-cancelled-notice">
                          <span className="cancelled-icon">❌</span>
                          <p>Este pedido ha sido <strong>cancelado</strong>.</p>
                        </div>
                      ) : (
                      <div className="pedido-progress">
                        {['Pendiente', 'Pagado', 'Enviado', 'Entregado'].map((label, i) => (
                          <div key={label} className={`progress-step ${i <= step ? 'active' : ''} ${i === step ? 'current' : ''}`}>
                            <div className="step-dot">
                              {i < step ? '✓' : i === step ? (
                                ['⏳', '✅', '🚚', '📦'][i]
                              ) : ''}
                            </div>
                            <span className="step-label">{label}</span>
                          </div>
                        ))}
                        <div className="progress-line">
                          <div className="progress-fill" style={{ width: `${(step / 3) * 100}%` }}></div>
                        </div>
                      </div>
                      )}

                      {/* Items del pedido */}
                      <div className="pedido-items-list">
                        <h4>Productos</h4>
                        {pedido.items?.map((item, idx) => (
                          <div key={idx} className="pedido-item">
                            <div className="item-img-wrapper">
                              {item.imagen ? (
                                <img src={item.imagen} alt={item.nombre} className="item-img" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                              ) : null}
                              <div className="item-img-placeholder" style={item.imagen ? { display: 'none' } : {}}>🎲</div>
                            </div>
                            <div className="item-info">
                              <span className="item-name">{item.nombre || `Producto #${item.producto_id}`}</span>
                              <span className="item-qty">Cant: {item.cantidad} × {formatCurrency(item.precio_unitario)}</span>
                            </div>
                            <div className="item-subtotal">{formatCurrency(item.subtotal)}</div>
                          </div>
                        ))}
                      </div>

                      {/* Resumen / Desglose para facturación */}
                      <div className="pedido-summary">
                        {pedido.subtotal > 0 && (
                          <div className="summary-row">
                            <span>Subtotal productos</span>
                            <span>{formatCurrency(pedido.subtotal)}</span>
                          </div>
                        )}
                        <div className="summary-row">
                          <span>Envío</span>
                          <span style={{ color: pedido.costo_envio === 0 ? 'var(--color-success)' : 'inherit' }}>
                            {pedido.costo_envio === 0 ? 'Gratis' : formatCurrency(pedido.costo_envio)}
                          </span>
                        </div>
                        {pedido.discount_amount > 0 && (
                          <div className="summary-row" style={{ color: 'var(--color-success)' }}>
                            <span>Descuento {pedido.coupon_code ? `(${pedido.coupon_code})` : ''}</span>
                            <span>-{formatCurrency(pedido.discount_amount)}</span>
                          </div>
                        )}
                        <div className="summary-row total">
                          <span>Total</span>
                          <span>{formatCurrency(pedido.total)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MisPedidosPage;
